import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api, money } from "@/lib/api";

const DOCS = [["slip", "Packing slip"], ["invoice", "Invoice"], ["label", "Shipping label"]];
const date = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
const Lines = ({ text }) => (text || "").split("\n").filter(Boolean).map((l) => <span key={l} className="block">{l}</span>);

const Address = ({ c }) => (
  <>
    <span className="block font-semibold">{c.first_name} {c.last_name}</span>
    <span className="block">{c.address1}</span>
    {c.address2 && <span className="block">{c.address2}</span>}
    <span className="block">{c.city}</span>
    <span className="block font-semibold tracking-wider">{c.postcode}</span>
    <span className="block">{c.country}</span>
  </>
);

const Head = ({ title, o, s }) => (
  <div className="flex justify-between items-start border-b-2 border-[var(--mm-red)] pb-6 mb-8">
    <div>
      <img src="/logo-mm2.png" alt="Maison Madison" className="h-16 w-auto" />
      <p className="text-[11px] mt-3 leading-relaxed text-[#5a4a45]"><Lines text={s.business_address} />{s.contact_email}<br />{s.instagram_handle}</p>
    </div>
    <div className="text-right">
      <p className="font-serif-mm text-4xl text-[var(--mm-red)]">{title}</p>
      <p className="text-xs mt-2">Order <b>{o.order_number}</b></p>
      <p className="text-xs">{date(o.created_at)}</p>
    </div>
  </div>
);

const ItemsTable = ({ o, prices }) => (
  <table className="w-full text-sm mb-6">
    <thead><tr className="border-b border-[#ccc] text-left text-[10px] uppercase tracking-[0.2em]">
      {!prices && <th className="py-2 w-8" />}<th className="py-2">Item</th><th>Size</th><th>Colour</th><th className="text-right">Qty</th>{prices && <><th className="text-right">Price</th><th className="text-right">Total</th></>}
    </tr></thead>
    <tbody>{o.items.map((it, i) => (
      <tr key={i} className="border-b border-[#eee]">
        {!prices && <td className="py-3"><span className="inline-block w-4 h-4 border border-[#333]" /></td>}
        <td className="py-3">{it.name}{it.preorder && <span className="text-[var(--mm-red)] text-xs"> (Pre-order)</span>}</td><td>{it.size}</td><td>{it.colour || "—"}</td><td className="text-right">{it.quantity}</td>
        {prices && <><td className="text-right">{money(it.price)}</td><td className="text-right">{money(it.price * it.quantity)}</td></>}
      </tr>
    ))}</tbody>
  </table>
);

const Slip = ({ o, s }) => (
  <div className="sheet-a4">
    <Head title="Packing slip" o={o} s={s} />
    <div className="grid grid-cols-2 gap-8 mb-8 text-sm"><div><p className="eyebrow mb-2">Ship to</p><Address c={o.customer} /></div><div><p className="eyebrow mb-2">Contact</p>{o.customer.email}<br />{o.customer.phone}</div></div>
    <ItemsTable o={o} prices={false} />
    <p className="font-serif-mm text-3xl mt-16">Thank you for choosing <em className="text-[var(--mm-red)]">Maison Madison.</em></p>
    <p className="text-xs text-[#5a4a45] mt-2">Made to be kept. Share your piece with us {s.instagram_handle}</p>
  </div>
);

const Invoice = ({ o, s }) => (
  <div className="sheet-a4">
    <Head title="Invoice" o={o} s={s} />
    <div className="grid grid-cols-2 gap-8 mb-8 text-sm"><div><p className="eyebrow mb-2">Billed to</p><Address c={o.customer} />{o.customer.email}</div><div className="text-right"><p className="eyebrow mb-2">Payment</p>{o.payment_status === "paid" ? "Paid in full" : "Unpaid"}<br />Invoice no. {o.order_number}</div></div>
    <ItemsTable o={o} prices />
    <div className="ml-auto w-64 text-sm space-y-1">
      <div className="flex justify-between"><span>Subtotal</span><span>{money(o.subtotal)}</span></div>
      <div className="flex justify-between"><span>Delivery</span><span>{o.shipping ? money(o.shipping) : "Complimentary"}</span></div>
      <div className="flex justify-between font-semibold text-base border-t border-[#333] pt-2 mt-2"><span>Total (GBP)</span><span>{money(o.total)}</span></div>
      <p className="text-[10px] text-[#5a4a45] text-right">All prices include VAT where applicable.</p>
    </div>
  </div>
);

const Label = ({ o, s }) => (
  <div className="sheet-label">
    <div className="flex justify-between items-start border-b-2 border-black pb-2">
      <div className="text-[9px] leading-tight"><p className="font-bold uppercase tracking-widest mb-0.5">From</p><p>Maison Madison</p><Lines text={s.business_address} /></div>
      <img src="/logo-mm2.png" alt="" className="h-9 w-auto" />
    </div>
    <div className="flex-1 py-4">
      <p className="text-[10px] font-bold uppercase tracking-widest mb-2">Deliver to</p>
      <div className="text-[19px] leading-snug"><Address c={o.customer} /></div>
      {o.customer.phone && <p className="text-xs mt-2">Tel: {o.customer.phone}</p>}
    </div>
    <div className="border-t-2 border-black pt-2 flex justify-between items-end text-[10px]">
      <div><p className="font-bold text-sm tracking-widest">{o.order_number}</p><p>{o.items.reduce((a, i) => a + i.quantity, 0)} item(s) · {date(o.created_at)}</p></div>
      <div className="w-28 h-12 border border-dashed border-black flex items-center justify-center text-[8px] uppercase tracking-widest text-center">Postage / tracking</div>
    </div>
  </div>
);

export default function PrintOrder() {
  const { orderNumber } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const doc = params.get("doc") || "slip";
  const [o, setO] = useState(null);
  const [s, setS] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!localStorage.getItem("mm_admin_token")) { navigate("/admin/login"); return; }
    Promise.all([api.get(`/admin/orders/${orderNumber}`), api.get("/settings")])
      .then(([a, b]) => { setO(a.data); setS(b.data); })
      .catch(() => setError("Could not load this order. Please log in again."));
  }, [orderNumber, navigate]);

  if (error) return <p data-testid="print-error" className="p-10">{error}</p>;
  if (!o || !s) return <p className="p-10">Loading…</p>;
  const Doc = { slip: Slip, invoice: Invoice, label: Label }[doc] || Slip;

  return (
    <div data-testid="print-order-page" className="min-h-screen bg-[#e9e4da] print:bg-white py-8 print:py-0">
      <style>{`@page { size: ${doc === "label" ? "4in 6in" : "A4"}; margin: ${doc === "label" ? "0" : "12mm"}; }`}</style>
      <div className="no-print max-w-[210mm] mx-auto mb-6 px-4 flex flex-wrap items-center gap-3">
        {DOCS.map(([k, l]) => <button key={k} data-testid={`print-doc-${k}`} onClick={() => setParams({ doc: k })} className={`eyebrow px-4 py-2 border ${doc === k ? "bg-[var(--mm-ink)] text-[var(--mm-cream)]" : "border-[var(--mm-ink)]"}`}>{l}</button>)}
        <button data-testid="print-now-button" onClick={() => window.print()} className="btn-mm ml-auto">Print</button>
      </div>
      <Doc o={o} s={s} />
    </div>
  );
}
